/**
 * operator-turn-telemetry.ts - BRE-5367.
 *
 * The shared contract for Operator experience telemetry in PostHog: event
 * names, the surface enum, and the pure arithmetic that turns a turn's raw
 * timeline into an ATTRIBUTED wait breakdown.
 *
 * WHY this is a shared module and not agent-service-local: the two halves of
 * the measurement are emitted from different runtimes. The browser owns
 * time-to-first-response (only it can see queueing, stream setup, and network);
 * agent-service owns the wait attribution (only it can see decode, tool
 * execution, compaction, and API retries). They join in PostHog on
 * `conversation_id`, which only works if both sides spell the event and
 * property names identically - so both sides import them from here.
 *
 * TWO NUMBERS, TWO NAMES (BRE-5523). This file used to call the browser event
 * "the headline", which stopped being true once denied turns were excluded from
 * latency by definition. There are two different headline numbers here and they
 * are NOT interchangeable, so each has a name used consistently throughout this
 * file and `docs/RUNBOOK-agent-tracing.md`:
 *
 *  - **The experience number** - `operator_turn_first_response.ttft_ms`. How
 *    long the user actually waited, end to end, including queueing, stream
 *    setup and network. It is the honest answer to "how did this FEEL", and the
 *    only number that sees anything outside the agent-service process. It is
 *    denial-INCLUSIVE and structurally cannot be otherwise: it carries no
 *    `outcome`, and a denial streams its canned message so the browser records
 *    it exactly like an answer.
 *  - **The latency percentile** - `operator_turn_completed.ttft_server_ms` /
 *    `turn_total_ms`, filtered by {@link OPERATOR_LATENCY_POPULATION_HOGQL}.
 *    The honest answer to "how long does ANSWERING take", because `outcome`
 *    sits on the same row and the denied and degraded turns can be removed.
 *    Any claim of the form "Operator latency is X" means this one.
 *
 * Neither replaces the other, and a number quoted without saying which it is
 * cannot be checked. Quoting the experience number against a refusal-rate
 * change is the specific error that motivated BRE-5523: refuse more often and
 * it improves.
 *
 * `conversation_id` is the ONLY key both sides carry. `trace_id` is the
 * per-turn `sessionId`, minted server-side and never sent to the browser, so it
 * appears on the turn event alone and a turn-level join between the two events
 * is not available - the correlation is per conversation. That is also why the
 * experience number cannot borrow `outcome` from the turn event. Getting one
 * would mean putting the trace id on the wire, which is a stream-format change, not a
 * telemetry one.
 *
 * WHY an interval ledger instead of summing durations: a turn's wall clock is
 * not the sum of its parts. Several tools dispatched in one turn execute
 * CONCURRENTLY, so adding their individual `read_ms` values double-counts the
 * overlap and produces a "total" larger than the turn itself. What a wait
 * breakdown must answer is "which labelled activity occupied each moment of
 * the user's wait", which is a set-union over intervals, not a sum. The ledger
 * below sweeps the turn's timeline, assigns every elementary segment to
 * exactly one bucket, and leaves whatever nothing claimed in
 * `unattributed_ms`. The buckets and the remainder therefore sum EXACTLY to
 * the turn's wall clock, by construction.
 *
 * WHY `unattributed_ms` is a first-class output rather than a rounding error:
 * it is the honest measure of how much of the user's wait this model cannot
 * yet explain. Folding the remainder into the nearest plausible bucket would
 * make the dashboard look complete while quietly hiding the exact stretches
 * worth investigating next (BRE-5157's "Working..." hang is precisely such a
 * stretch). A growing `unattributed_ms` is a finding, not noise.
 */

import type { SkillsDeliveryEvidence, TurnSkillEvidence } from "./skill-evidence";

/**
 * PostHog event names. Stable identifiers - the prod dashboard and any alert
 * built on them reference these strings, so renaming one silently breaks every
 * saved insight. Add a new event rather than repurposing an existing name.
 */
export const OPERATOR_TELEMETRY_EVENTS = {
  /**
   * Emitted by the BROWSER when the first visible token or tool call of a turn
   * reaches the user. This carries **the experience number** (see the two-names
   * note in this file's header): it is anchored at the moment the request left
   * the client, so it includes stream setup, the agent-service concurrency
   * gate, MCP connect, and network - all the things a server-side clock
   * structurally cannot see.
   *
   * It is NOT **the latency percentile**, and cannot be made into one: it
   * carries no `outcome`, and a Tier-1 denial streams its canned message so the
   * browser records it exactly like a real answer (BRE-5523). Quote it for how
   * a turn felt, never as "Operator latency".
   */
  TURN_FIRST_RESPONSE: "operator_turn_first_response",
  // At most two per web turn: visible DOM text after a frame, not a paint/attention guarantee.
  TURN_CONTENT_PRESENTED: "operator_turn_content_presented",
  TURN_PRESENTATION_DIAGNOSTIC: "operator_turn_presentation_diagnostic",
  /**
   * Emitted by AGENT-SERVICE when a turn finishes (successfully or not),
   * carrying the wait attribution and the tool rollup. One per turn, on every
   * surface - web, Slack, Teams, and scheduled runs all flow through the same
   * `/chat` loop.
   */
  TURN_COMPLETED: "operator_turn_completed",
  /**
   * BRE-6777: emitted by the BROWSER when a send found its own token stale and
   * re-minted before the request left, with whether that mint succeeded.
   *
   * The positive signal for a guard that is otherwise invisible when it works:
   * once it does its job, agent-service stops seeing expired tokens, so the
   * server-side evidence can only ever show the guard FAILING. Without this,
   * "the guard never ran" and "the guard ran and the mint failed" are the same
   * empty result.
   */
  TURN_STALE_SEND_REMINT: "operator_turn_stale_send_remint",
} as const;

/**
 * Which product surface the turn ran on. Slack/Teams/scheduled turns reach
 * `/chat` through the same channel-neutral runner as a web turn, so without
 * this every bot turn would be indistinguishable from a user sitting in front
 * of the chat - and a Slack turn's latency budget is a completely different
 * conversation from a browser turn's.
 */
export const OPERATOR_SURFACES = [
  "web",
  "slack",
  "teams",
  /** An unattended run from the agent's own schedule - nobody is watching. */
  "scheduled",
  /**
   * A background fill re-entering `/chat` on the user's behalf (a chart tile
   * filling in, an artifact body being written). Nobody typed anything, but the
   * user IS waiting on the result, so it belongs on the experience dashboard -
   * just never mixed into the typed-a-message TTFT distribution.
   */
  "headless",
  /**
   * A synthetic turn driven by the replay harness - a load-profile probe or a
   * frozen-case eval. Nobody is waiting on it and no human ever sees it.
   *
   * This member is what keeps the harness from corrupting the thing it measures
   * (BRE-5519). A replay sends no `surface` unless one is chosen for it, and an
   * absent surface parses to `"web"` - so a profile running at concurrency would
   * pour hundreds of synthetic turns into the browser-turn bucket and move the
   * very p50 it was run to observe. The enum comment above already states the
   * principle for bot turns; a replay is the same hazard with a faster clock.
   *
   * It is the latency counterpart of BRE-5159's sweep exclusion: that predicate
   * keeps replay traffic out of the QUALITY corpus, and this keeps it out of the
   * LATENCY distribution. Both are needed, because they are different pipelines
   * reading the same conversations.
   */
  "replay",
  /**
   * BRE-6666: the native iOS Recorder app's Chat tab. A person is watching a
   * streamed reply exactly as on `web`, but its latency and outcomes must be
   * a slice of their own — the phone's network is a different population, and
   * folding it into `web` would move the browser numbers without anyone
   * seeing why.
   */
  "ios",
  /**
   * BRE-8547: the macOS Recorder app's Operator window. Its own slice for the
   * reason `ios` has one: a desktop app's network and wait are neither the
   * browser's nor the phone's.
   */
  "macos",
  /**
   * A caller sent a `surface` this build does not recognise. Deliberately a
   * REAL bucket rather than a silent fallback to "web": mislabelling a bot turn
   * as a browser turn corrupts the very cut the field exists to enable, and an
   * "unknown" slice showing up on the dashboard is a self-announcing bug.
   */
  "unknown",
] as const;

export type OperatorSurface = (typeof OPERATOR_SURFACES)[number];

/**
 * Validate a caller-supplied `surface`. Returns "web" when the field is absent
 * (every pre-BRE-5367 client), and "unknown" when it is present but not a
 * member - see the enum comment for why those two cases must not collapse
 * together.
 */
export function parseOperatorSurface(value: unknown): OperatorSurface {
  if (value === undefined || value === null) return "web";
  return (OPERATOR_SURFACES as readonly string[]).includes(value as string)
    ? (value as OperatorSurface)
    : "unknown";
}

/**
 * BRE-9375: Tier 1 is an in-process agent-service turn, Tier 2 an E2B sandbox
 * run. Spelled as `AI_COWORKER_LANES` spells them so the two dashboards join.
 */
export const OPERATOR_TURN_TIERS = ["tier1", "tier2"] as const;
export type OperatorTurnTier = (typeof OPERATOR_TURN_TIERS)[number];

/**
 * BRE-9375: the latency population a turn belongs to. A one-line reply and a
 * multi-tool build are different waits, and one percentile over both moves with
 * the mix rather than the speed. `background` (nobody is waiting) and `fill` (a
 * headless re-entry nobody typed) stay out of the typed-message headline.
 */
export const OPERATOR_LATENCY_LANES = ["quick", "tools", "build", "sandbox", "fill", "background"] as const;
export type OperatorLatencyLane = (typeof OPERATOR_LATENCY_LANES)[number];

/** BRE-9375: first match wins, so who-is-waiting outranks what the turn did. */
export function classifyLatencyLane(turn: {
  tier: OperatorTurnTier;
  surface: OperatorSurface;
  toolCount: number;
  artifactWriteCalls: number;
  delegated: boolean;
}): OperatorLatencyLane {
  if (turn.surface === "scheduled" || turn.surface === "replay") return "background";
  if (turn.surface === "headless") return "fill";
  if (turn.tier === "tier2") return "sandbox";
  if (turn.artifactWriteCalls > 0 || turn.delegated) return "build";
  if (turn.toolCount > 0) return "tools";
  return "quick";
}

/**
 * The labelled activities a moment of the user's wait can be attributed to.
 * Each is something we MEASURED an interval for - "why was this turn slow" is
 * answered by which of these owned the most wall clock.
 */
export const OPERATOR_WAIT_BUCKETS = [
  /** Waiting for the Brev MCP server to connect before the prompt can start. */
  "mcp_connect",
  /**
   * Between the turn starting (or the last tool result being handed back) and
   * the model's response beginning to stream. This is where uncached context
   * prefill, API retry/backoff, and request queueing live - they are not
   * separately timeable from the SDK message stream, so the bucket is measured
   * as one span and DISAMBIGUATED by the signals on the event (see
   * {@link resolveDominantWaitCause}).
   */
  "pre_model",
  /** The model generating: thinking, text, and tool-call JSON. */
  "decode",
  /** Tools executing, from dispatch to the last result of that batch. */
  "tool",
  /** The SDK compacting the conversation to fit the context window. */
  "compaction",
] as const;

export type OperatorWaitBucket = (typeof OPERATOR_WAIT_BUCKETS)[number];

/**
 * What `dominant_wait_cause` can report. A superset of the buckets: `pre_model`
 * is refined into `prefill` or `retry` when the turn's signals say which one it
 * was, and `unattributed` is reported honestly when the largest stretch of the
 * wait is one nothing accounted for.
 */
export type OperatorWaitCause =
  | OperatorWaitBucket
  | "prefill"
  | "retry"
  | "unattributed";

/**
 * Bucket precedence when two measured intervals overlap. Overlap should be rare
 * (the buckets describe phases that mostly alternate), but the SDK is free to
 * interleave and the ledger must still produce a partition rather than
 * double-count. Earlier entries win.
 *
 * The order encodes which label is more INFORMATIVE about the same moment:
 * compaction and MCP connect are specific, externally-caused stalls and are
 * what you would want named; tool execution beats decode because a tool result
 * arriving during a still-open decode window is the tool's time, not the
 * model's; `pre_model` is last because it is defined as the residual gap and
 * should never displace something we identified positively.
 */
const BUCKET_PRECEDENCE: readonly OperatorWaitBucket[] = [
  "compaction",
  "mcp_connect",
  "tool",
  "decode",
  "pre_model",
];

/** One measured stretch of the turn's timeline, in epoch ms. */
export interface OperatorWaitInterval {
  bucket: OperatorWaitBucket;
  startMs: number;
  endMs: number;
}

export interface OperatorWaitSummary {
  /** The turn's wall clock: `windowEndMs - windowStartMs`, floored at 0. */
  totalMs: number;
  /** Per-bucket occupied time. Every bucket is present, zero when unused. */
  buckets: Record<OperatorWaitBucket, number>;
  /** Wall clock no interval claimed. `sum(buckets) + unattributedMs === totalMs`. */
  unattributedMs: number;
}

function emptyBuckets(): Record<OperatorWaitBucket, number> {
  return {
    mcp_connect: 0,
    pre_model: 0,
    decode: 0,
    tool: 0,
    compaction: 0,
  };
}

/**
 * Partition `[windowStartMs, windowEndMs)` across the buckets the intervals
 * claim, resolving overlap by {@link BUCKET_PRECEDENCE} and reporting anything
 * unclaimed as `unattributedMs`.
 *
 * Pure and total: intervals outside the window are clipped, inverted or
 * zero-length intervals are dropped, and a non-positive window yields all
 * zeros. The postcondition `sum(buckets) + unattributedMs === totalMs` holds
 * for every input, which is what lets the dashboard render the breakdown as a
 * stacked bar that always adds up.
 *
 * The sweep is O(n^2) in the number of intervals. n is the count of model turns
 * plus tool batches in a single agent turn - tens at most - so the simplicity
 * is worth more here than an interval tree would be.
 */
export function summarizeWaitIntervals(
  intervals: readonly OperatorWaitInterval[],
  windowStartMs: number,
  windowEndMs: number,
): OperatorWaitSummary {
  const totalMs = Math.max(0, windowEndMs - windowStartMs);
  const buckets = emptyBuckets();
  if (totalMs === 0) return { totalMs, buckets, unattributedMs: 0 };

  const clipped = intervals
    .map((interval) => ({
      bucket: interval.bucket,
      startMs: Math.max(windowStartMs, Math.min(interval.startMs, windowEndMs)),
      endMs: Math.max(windowStartMs, Math.min(interval.endMs, windowEndMs)),
    }))
    .filter((interval) => interval.endMs > interval.startMs);

  // Elementary segments: between consecutive boundaries no interval can start
  // or end, so a single precedence decision covers the whole segment.
  const boundaries = Array.from(
    new Set([
      windowStartMs,
      windowEndMs,
      ...clipped.map((i) => i.startMs),
      ...clipped.map((i) => i.endMs),
    ]),
  ).sort((a, b) => a - b);

  let unattributedMs = 0;
  for (let i = 0; i < boundaries.length - 1; i += 1) {
    const segmentStart = boundaries[i];
    const segmentEnd = boundaries[i + 1];
    const segmentMs = segmentEnd - segmentStart;
    if (segmentMs <= 0) continue;

    let winner: OperatorWaitBucket | null = null;
    let winnerRank = Number.POSITIVE_INFINITY;
    for (const interval of clipped) {
      if (interval.startMs > segmentStart || interval.endMs < segmentEnd) {
        continue;
      }
      const rank = BUCKET_PRECEDENCE.indexOf(interval.bucket);
      if (rank !== -1 && rank < winnerRank) {
        winnerRank = rank;
        winner = interval.bucket;
      }
    }

    if (winner) buckets[winner] += segmentMs;
    else unattributedMs += segmentMs;
  }

  return { totalMs, buckets, unattributedMs };
}

/**
 * Uncached prefill big enough to be a plausible explanation for a long
 * pre-model gap. Below this, a slow `pre_model` stretch is more likely queueing
 * or dispatch than context re-prefill, and claiming "prefill" would send the
 * next investigation down the wrong path.
 *
 * Calibrated against the BRE-4647 finding that the pauses worth explaining came
 * with tens of thousands of uncached input tokens; a few thousand tokens
 * prefills far too fast to be the dominant cost of a multi-second gap.
 */
export const OPERATOR_PREFILL_TOKEN_FLOOR = 10_000;

/** Turn-level signals that disambiguate a `pre_model` stretch. */
export interface OperatorWaitSignals {
  /** SDK `api_retry` system messages seen this turn. */
  apiRetryCount: number;
  /**
   * Input tokens on model requests that read ~nothing from the prompt cache -
   * the prefill fingerprint from BRE-4653's span design.
   */
  uncachedPrefillTokens: number;
  /**
   * BRE-5733: `input + cache_read + cache_creation` on the turn's FIRST
   * `message_start` - the opening request's true prompt size.
   *
   * Exists to settle one question the `agent_context_floor` probe structurally
   * cannot. That probe reports `total_tokens` from the SDK, which sums its
   * category list, and the MCP tools category is loaded PLUS deferred - so it
   * counts ~85K of tool definitions that deferral kept out of the request. The
   * probe also fires before the first request is billed, so its own `apiUsage`
   * is absent. Only a real `message_start` states what was sent.
   *
   * The comparison, in one query beside `compaction_count`:
   *
   *   - far BELOW the probe's `total_tokens` -> the request is lean, the
   *     occupancy figure overstates it, and the question becomes whether
   *     autocompact reads the inflated number.
   *   - NEAR it -> the definitions are going out and `mcpTools[].isLoaded` is
   *     not telling us what we think.
   *
   * Optional, and OMITTED rather than zeroed when the turn never reached the
   * model (shed, stalled, no MCP): a real zero and "there was no request" must
   * not average together, and a turn that never called the API is exactly the
   * kind that would drag the median toward a false answer.
   */
  firstRequestPromptTokens?: number;
  /**
   * BRE-7987: prompt-cache read and write tokens for the whole turn tree
   * (subagents included), from the terminal result's `modelUsage`. A write with
   * no read is a cache miss, which was visible only in CloudWatch
   * `agent_turn_usage`. Omitted, not zeroed, when no result arrived.
   */
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
}

/**
 * Name the single biggest contributor to the user's wait.
 *
 * Ties go to the earlier entry in {@link BUCKET_PRECEDENCE}, so a turn split
 * exactly between two causes reports the more specific one rather than
 * flip-flopping between runs.
 *
 * `pre_model` is refined only when a signal actually supports the refinement.
 * Retry wins over prefill when both are present: a retry is a hard, externally
 * imposed delay we can act on directly, whereas large uncached prefill is
 * always somewhat true of a big conversation and would otherwise mask it.
 * With no supporting signal the cause stays `pre_model` - an honest "the gap
 * was before the model responded, and we do not know why" beats a guess.
 */
export function resolveDominantWaitCause(
  summary: OperatorWaitSummary,
  signals: OperatorWaitSignals,
): OperatorWaitCause {
  let cause: OperatorWaitCause = "unattributed";
  let best = summary.unattributedMs;

  for (const bucket of BUCKET_PRECEDENCE) {
    if (summary.buckets[bucket] > best) {
      best = summary.buckets[bucket];
      cause = bucket;
    }
  }

  if (cause !== "pre_model") return cause;
  if (signals.apiRetryCount > 0) return "retry";
  if (signals.uncachedPrefillTokens >= OPERATOR_PREFILL_TOKEN_FLOOR) {
    return "prefill";
  }
  return "pre_model";
}

/**
 * How a turn ended. Drives the "did the experience actually work" cut.
 *
 * `denied` is its own outcome rather than a flavour of `completed` because a
 * precondition denial answers in well under a second without ever reaching the
 * model. Counting those as completed turns would seed the latency distribution
 * with near-zero rows that no user experienced as an answer, dragging the
 * percentiles down and making the dashboard report an improvement whenever
 * denials rise.
 *
 * Declared as a runtime array with the type DERIVED from it (BRE-5523) so the
 * latency/non-delivery partition below can be checked for exhaustiveness by a
 * test: a sixth outcome added to a bare type union would compile everywhere and
 * simply go missing from both metric definitions.
 */
export const OPERATOR_TURN_OUTCOMES = [
  "completed",
  "denied",
  "max_turns",
  "stalled",
  "error",
  // BRE-5699: the brev MCP server did not connect in time, so the tools this
  // turn REQUIRED were absent and we answered directly instead of running a
  // model call that could not have succeeded. Deliberately its own value rather
  // than folded into `denied` or `error`: `denied` is a Tier-1 precondition the
  // USER can fix (connect Jira, pick an org), and this is our own infrastructure
  // missing its deadline. Filing it under a user-fixable refusal would hide an
  // availability regression inside a number that is expected to be non-zero.
  "tools_unavailable",
] as const;
export type OperatorTurnOutcome = (typeof OPERATOR_TURN_OUTCOMES)[number];

/**
 * How the response stream finished, as the BROWSER saw it - the `stream_end`
 * property on `operator_turn_first_response`.
 *
 * `rejected` never opened one (a 4xx/5xx before any bytes). `cancelled` is the
 * user pressing stop, or navigating away mid-answer - the clearest signal we
 * have that a turn ran out of their patience rather than out of work.
 *
 * Declared here rather than in the frontend (BRE-5523) because the latency
 * population is defined across BOTH events, and the two definitions have to be
 * readable side by side.
 *
 * **The latency percentile** is not this event: it reads
 * `operator_turn_completed.ttft_server_ms` / `turn_total_ms`, filtered by
 * `outcome` via {@link OPERATOR_LATENCY_POPULATION_HOGQL} - the only event that
 * carries the outcome on the same row.
 * This event carries **the experience number**, which is denial-INCLUSIVE: it
 * is the only number covering queueing and network, and it cannot exclude a
 * denial, because a denial streams its canned message and reads
 * `stream_end = 'complete'` exactly like an answer. Deciding what this event's
 * rows can honestly say therefore needs this union and
 * {@link OperatorTurnOutcome} in one place. See
 * {@link OPERATOR_CLIENT_STREAM_END_LATENCY_HOGQL} and the two-names note in
 * this file's header.
 *
 * The frontend re-exports it under its old name.
 */
export const OPERATOR_STREAM_END_VALUES = [
  "complete",
  "error",
  "cancelled",
  "rejected",
] as const;
export type OperatorStreamEnd = (typeof OPERATOR_STREAM_END_VALUES)[number];

/**
 * BRE-7398: set by agent-service ONLY on a turn it refused before starting it
 * (draining, or at its concurrency cap). Its presence is the one signal that a
 * re-send cannot run the agent loop twice. Values name the reason.
 */
export const TURN_REFUSED_HEADER = "X-Brev-Turn-Refused";
export type TurnRefusedReason = "draining" | "capacity";

/**
 * BRE-5685 - WHY a turn failed, as the browser saw it: the `failure_kind`
 * property on `operator_turn_first_response`.
 *
 * `stream_end` above says WHERE a turn stopped; it does not say why, so every
 * distinct fault - an expired session, a dead connection, an overloaded
 * agent-service, a 429 - collapses into the single value `rejected`. That is
 * the gap this closes: the error rate was measurable but not diagnosable, and
 * the one user-visible symptom (the red "that turn got cut off" block) is
 * identical for all of them.
 *
 * Declared beside {@link OPERATOR_STREAM_END_VALUES} for the same reason that
 * union lives here rather than in the frontend: the two describe the same
 * event's ending and are read side by side, and the partition below is a
 * DEFINITION the product depends on, not a frontend rendering detail.
 *
 * A runtime array with the type derived from it so
 * {@link isPreStreamFailure} can be checked for exhaustiveness - a kind added
 * to a bare type union would compile everywhere and silently fall into the
 * "might have run" half, which is the safe direction but would go unnoticed.
 */
export const OPERATOR_TURN_FAILURE_KINDS = [
  /** The browser reports no network. Nothing left the machine. */
  "offline",
  /** A request was attempted but no response ever came back (DNS, TLS, dropped connect). */
  "unreachable",
  /** 401/403 that survived the transport's re-mint - the session identity is stale. */
  "auth",
  /** 429 - too many turns, too fast. */
  "rate_limited",
  /**
   * 502/503/504 with no refusal header. Authored by the edge, which cannot say
   * whether it forwarded the request: a 504 in particular means it DID.
   */
  "overloaded",
  /** Any other 5xx. */
  "server",
  /** Any other 4xx. The request was understood and refused; repeating it changes nothing. */
  "rejected",
  /**
   * The stream OPENED and then died mid-answer. Distinct from every value above
   * because the turn reached the agent loop: tools may have run and entities may
   * already exist, so this is the one failure a retry can duplicate.
   */
  "interrupted",
  /**
   * BRE-6887: the Operator's model call failed mid-turn on a transient cause
   * (rate limit, overload, server fault). Not pre-stream: tools may have run.
   */
  "model_busy",
  /** BRE-6887: the model call failed on a cause a resend will not fix (credentials, billing, config). */
  "model_unavailable",
  /** Classified nothing. A real bucket, so an unrecognised fault is visible rather than mislabelled. */
  "unknown",
  /**
   * BRE-7398: agent-service itself refused the turn before starting it, and said
   * so with {@link TURN_REFUSED_HEADER}. The only failure that PROVES the agent
   * loop did not run; an edge status never can.
   */
  "refused",
  /**
   * BRE-4735: 402, the `agent_chat` gate refused the turn before it ran because
   * the workspace is out of credits or past its trial. Only an admin can fix it.
   */
  "billing",
  /**
   * BRE-8412: the `agent_chat` gate refused the turn before it ran because the
   * member's role has no `run_agent` (OBSERVER, GUEST). Only an admin can fix it.
   */
  "not_permitted",
  /**
   * BRE-8412: 429, the gate refused the turn before it ran because the member
   * already has their usage profile's turns in flight. Sending again once one
   * finishes works.
   */
  "member_turn_limit",
] as const;
export type OperatorTurnFailureKind =
  (typeof OPERATOR_TURN_FAILURE_KINDS)[number];

/**
 * True when the failure PROVABLY happened before the agent loop ran, so a
 * re-send cannot duplicate a side effect (the loop creates goals, writes
 * artifacts, sends messages). The burden is on proof, not likelihood.
 *
 * BRE-7398: since BRE-7172 a turn keeps running when its client disconnects,
 * so a dropped connection or an edge status (`unreachable`, `overloaded`,
 * `server`, `rate_limited`) cannot say whether it got through. Only our own
 * refusal, a browser that was offline, and a rejected identity can. `rejected`
 * is safe but pointless to repeat; `unknown` has not been shown safe.
 * Exhaustive with NO default branch, so a new kind fails the build here.
 */
export function isPreStreamFailure(kind: OperatorTurnFailureKind): boolean {
  switch (kind) {
    case "offline":
    case "auth":
    case "refused":
    case "billing":
    case "not_permitted":
    case "member_turn_limit":
      return true;
    case "unreachable":
    case "rate_limited":
    case "overloaded":
    case "server":
    case "rejected":
    case "interrupted":
    case "model_busy":
    case "model_unavailable":
    case "unknown":
      return false;
  }
}

/**
 * BRE-6887: the failure kinds agent-service can name for a turn its model call
 * ended. `rejected` covers a request the model refused as sent (too long, a bad
 * image), where only the user's next message can help.
 */
export type ModelCallFailureKind = Extract<
  OperatorTurnFailureKind,
  "model_busy" | "model_unavailable" | "rejected" | "interrupted"
>;

const MODEL_CALL_FAILURE_PREFIX = "operator-model-call-failed:";
const MODEL_CALL_FAILURE_KINDS: ReadonlySet<string> = new Set<ModelCallFailureKind>([
  "model_busy",
  "model_unavailable",
  "rejected",
  "interrupted",
]);

/**
 * The `errorText` agent-service puts on the turn's stream `error` chunk: a
 * machine-readable kind and no prose, so every `/chat` consumer still sees a
 * failed turn and nothing the SDK said reaches the wire.
 */
export function modelCallFailureErrorText(kind: ModelCallFailureKind): string {
  return `${MODEL_CALL_FAILURE_PREFIX}${kind}`;
}

/** Read {@link modelCallFailureErrorText} back; null for any other error text. */
export function parseModelCallFailure(
  errorText: string | null | undefined,
): ModelCallFailureKind | null {
  if (!errorText?.startsWith(MODEL_CALL_FAILURE_PREFIX)) return null;
  const kind = errorText.slice(MODEL_CALL_FAILURE_PREFIX.length);
  return MODEL_CALL_FAILURE_KINDS.has(kind) ? (kind as ModelCallFailureKind) : null;
}

/**
 * Map an HTTP status to a failure kind. Split out from the browser-side
 * classifier so the mapping is pure, shared, and testable on its own.
 *
 * 2xx never reaches here - a successful status that fails later is an
 * `interrupted` stream, which the caller determines from where it broke, not
 * from the status.
 */
export function failureKindForStatus(status: number): OperatorTurnFailureKind {
  if (status === 401 || status === 403) return "auth";
  if (status === 402) return "billing";
  if (status === 429) return "rate_limited";
  if (status === 502 || status === 503 || status === 504) return "overloaded";
  if (status >= 500) return "server";
  if (status >= 400) return "rejected";
  return "unknown";
}

/**
 * Which turns a latency percentile is allowed to describe (BRE-5523).
 *
 * A percentile answers "how long did an answer take". Every outcome below that
 * is NOT `completed` is a turn that produced no answer, and each distorts the
 * distribution in a different direction:
 *
 *  - `denied` short-circuits before the model in well under a second, so it
 *    drags percentiles DOWN. The dashboard then improves whenever we refuse
 *    more often, which is the exact opposite of the truth.
 *  - `stalled` and `max_turns` end at a watchdog/loop bound rather than at an
 *    answer, so they pull percentiles UP toward a constant nobody experienced
 *    as latency.
 *  - `error` ends wherever the failure happened, which is uncorrelated with how
 *    long answering takes.
 *
 * So refusal rate and latency become independent axes, each readable on its
 * own: latency over {@link OPERATOR_LATENCY_ELIGIBLE_OUTCOMES}, non-delivery
 * rate over {@link OPERATOR_NON_DELIVERY_OUTCOMES} out of every turn. Neither
 * number can flatter itself by moving turns into the other set.
 *
 * This is a DEFINITION, not a filter applied at emit time. `operator_turn_completed`
 * still fires for all five outcomes - a turn that hung is the single most
 * important row we have, and dropping it at the source would make the worst
 * experiences invisible. The exclusion belongs in the query.
 */
export const OPERATOR_LATENCY_ELIGIBLE_OUTCOMES = [
  "completed",
] as const satisfies readonly OperatorTurnOutcome[];

/** Tier-1 precondition refusals - the turns we declined to run. */
export const OPERATOR_DENIED_OUTCOMES = [
  "denied",
] as const satisfies readonly OperatorTurnOutcome[];

/**
 * Turns that ran but did not deliver an answer.
 *
 * Grouped as one set because they share the property that makes them unfit for
 * a latency cut - the elapsed time measures how the turn broke, not how long
 * answering took - while remaining individually reportable.
 */
export const OPERATOR_DEGRADED_OUTCOMES = [
  "max_turns",
  "stalled",
  "error",
  // BRE-5699: degraded rather than denied - the turn did not deliver an answer
  // because OUR dependency was late, and its elapsed time measures the connect
  // wait rather than how long answering took, which is exactly the property
  // this set exists to capture.
  "tools_unavailable",
] as const satisfies readonly OperatorTurnOutcome[];

/**
 * Denied ∪ degraded - the numerator of the non-delivery (refusal) rate.
 *
 * Every turn excluded from the latency set is counted HERE. The two sets
 * partition the outcome union exactly, which is what stops a turn from
 * disappearing out of both.
 */
export const OPERATOR_NON_DELIVERY_OUTCOMES = [
  ...OPERATOR_DENIED_OUTCOMES,
  ...OPERATOR_DEGRADED_OUTCOMES,
] as const satisfies readonly OperatorTurnOutcome[];

/** Which side of the latency/non-delivery split an outcome falls on. */
export type OperatorOutcomeClass = "latency" | "denied" | "degraded";

/**
 * Classify one outcome. Exhaustive on purpose and with NO default branch: a
 * sixth outcome added to the union fails the build here rather than silently
 * defaulting into (or out of) the latency population.
 */
export function classifyOperatorTurnOutcome(
  outcome: OperatorTurnOutcome,
): OperatorOutcomeClass {
  switch (outcome) {
    case "completed":
      return "latency";
    case "denied":
      return "denied";
    case "max_turns":
    case "stalled":
    case "error":
    case "tools_unavailable":
      return "degraded";
  }
}

/** True when this turn belongs in a latency percentile. */
export function isLatencyEligibleOutcome(outcome: OperatorTurnOutcome): boolean {
  return classifyOperatorTurnOutcome(outcome) === "latency";
}

/** True when this turn belongs in the non-delivery (refusal) rate's numerator. */
export function isNonDeliveryOutcome(outcome: OperatorTurnOutcome): boolean {
  return classifyOperatorTurnOutcome(outcome) !== "latency";
}

/**
 * Render an outcome set as a HogQL predicate over `operator_turn_completed`.
 *
 * The metric definitions in `docs/RUNBOOK-agent-tracing.md` and the saved
 * PostHog insights quote the output of this function rather than hand-writing
 * the list, so an outcome added to the union cannot leave one query behind (a
 * drift test asserts the runbook still matches). Interpolation is safe by
 * construction: every value comes from the closed union above, never from
 * input.
 */
export function hogQlOutcomeFilter(
  outcomes: readonly OperatorTurnOutcome[],
): string {
  return `properties.outcome IN (${outcomes.map((o) => `'${o}'`).join(", ")})`;
}

/**
 * Surfaces that are not traffic at all, but the measuring apparatus (BRE-5519,
 * folded in on rebase).
 *
 * `replay` is a synthetic turn from the replay harness - a load-profile probe
 * or a frozen-case eval. Nobody is waiting on it and no human ever sees it, so
 * it is not an experience and cannot appear in a number describing experiences.
 * Leaving it in would let the harness move the very p50 it is run to observe:
 * a profile at concurrency emits hundreds of `operator_turn_completed` rows in
 * minutes, all of which satisfy `outcome = 'completed'`.
 *
 * A LIST rather than a bare `!= 'replay'` so the next synthetic surface joins
 * one place, and the drift test can assert the runbook names all of them.
 *
 * Deliberately NOT excluded here, because they are real product turns and
 * hiding them would be its own dishonesty:
 *  - `scheduled` and `headless` - nobody typed, but the work is real and a user
 *    is often waiting on the result. The runbook's rule for these is to CUT by
 *    `surface`, never to drop them; excluding them by definition would conceal
 *    real product latency.
 *  - `unknown` - a caller sending a surface this build does not know is a
 *    self-announcing BUG. Excluding it would hide exactly what it exists to
 *    reveal.
 */
export const OPERATOR_SYNTHETIC_SURFACES = [
  "replay",
] as const satisfies readonly OperatorSurface[];

/**
 * Render a surface exclusion as a HogQL predicate over `operator_turn_completed`.
 *
 * Same generation-not-transcription contract as {@link hogQlOutcomeFilter}, and
 * safe by construction for the same reason: every value comes from the closed
 * {@link OPERATOR_SURFACES} union, never from input.
 */
export function hogQlSurfaceExclusion(
  surfaces: readonly OperatorSurface[],
): string {
  return `properties.surface NOT IN (${surfaces.map((s) => `'${s}'`).join(", ")})`;
}

/**
 * The corpus: turns that are somebody's actual experience.
 *
 * This is a SEPARATE AXIS from the outcome split below, and composed into both
 * halves of it rather than folded into one. Three reasons, and the second is
 * the one that would bite (BRE-5523 rebase decision):
 *
 *  1. They answer different questions. `outcome` asks "did this turn deliver an
 *     answer"; `surface` asks "is this a real user's turn at all". One opaque
 *     string would leave a reader unable to tell which rule dropped a row.
 *  2. **The refusal rate needs it just as much as latency does.** A replay turn
 *     can be denied or stalled like any other, so excluding replay from latency
 *     alone would let a load profile inflate the non-delivery rate while
 *     deflating nothing - and the two axes would stop describing the same
 *     population, which is precisely the "two numbers that cannot be compared"
 *     defect this ticket exists to remove. Both carry it, or neither should.
 *  3. It mirrors the SQL side of this same ticket: `conversationInOperatorCorpus`
 *     carries the population (archived / scheduled / replay out) and the window,
 *     and the verdict logic partitions WITHIN it. Same shape on both pipelines,
 *     which is what BRE-5519's note means by "different pipelines reading the
 *     same conversations".
 */
export const OPERATOR_REAL_TRAFFIC_HOGQL = hogQlSurfaceExclusion(
  OPERATOR_SYNTHETIC_SURFACES,
);

/**
 * The filter every Operator latency percentile must carry.
 *
 * Corpus AND partition: real traffic, and of that, the turns that delivered.
 */
export const OPERATOR_LATENCY_POPULATION_HOGQL = `${OPERATOR_REAL_TRAFFIC_HOGQL} AND ${hogQlOutcomeFilter(
  OPERATOR_LATENCY_ELIGIBLE_OUTCOMES,
)}`;

/**
 * The filter the non-delivery (refusal) rate's numerator must carry.
 *
 * The SAME corpus as the latency population - see reason 2 above. Its
 * denominator is every turn passing {@link OPERATOR_REAL_TRAFFIC_HOGQL}, not
 * every row in the table.
 */
export const OPERATOR_NON_DELIVERY_HOGQL = `${OPERATOR_REAL_TRAFFIC_HOGQL} AND ${hogQlOutcomeFilter(
  OPERATOR_NON_DELIVERY_OUTCOMES,
)}`;

/**
 * The best the CLIENT event can do on its own, which is not good enough to be
 * **the latency percentile** (BRE-5523).
 *
 * `operator_turn_first_response` cannot see `outcome`. A Tier-1 denial still
 * streams its canned message, so the browser records it as
 * `stream_end = 'complete'`, `no_response = false` - indistinguishable from a
 * real answer, and near-instant. This predicate therefore removes only the
 * turns the browser itself knows produced nothing; every denial stays in.
 *
 * And it CANNOT be rescued by joining: the two events share only
 * `conversation_id` (see this file's header). `trace_id` is minted server-side
 * and never sent to the browser, so there is no per-turn join to carry
 * `outcome` across, and a per-conversation join would attach one turn's outcome
 * to another turn's timing. Closing that would mean putting the trace id on the
 * wire - a new property on the stream, which this ticket does not add. Written
 * down rather than built: see BRE-5523's PR body.
 *
 * The consequence is the definition itself, not a caveat on it. **The latency
 * percentile is read off `operator_turn_completed`**, where `ttft_server_ms` /
 * `turn_total_ms` sit on the SAME ROW as `outcome`, so
 * {@link OPERATOR_LATENCY_POPULATION_HOGQL} applies directly and no join is
 * involved. This event keeps **the experience number**, captioned as including
 * denials - it is the only number that covers queueing and network, and
 * dropping it would trade one blind spot for another.
 *
 * NO synthetic-surface exclusion here, and that is a finding rather than an
 * omission (BRE-5523 rebase). {@link OPERATOR_REAL_TRAFFIC_HOGQL} keeps replay
 * traffic out of the server-side numbers, but this event structurally cannot
 * carry a replay row: the harness is a server-side `fetch` to `/chat`
 * (`packages/functions/src/agent/replayChatTransport.ts`) that never runs a
 * browser, and this event is emitted only by a browser, which hardcodes
 * `surface: "web"`. Adding the filter anyway would be dead and would imply a
 * hazard that does not exist. A guard test pins the hardcoding, because the day
 * that event learns to send a caller-supplied surface, this reasoning expires.
 */
export const OPERATOR_CLIENT_STREAM_END_LATENCY_HOGQL =
  "properties.stream_end = 'complete' AND properties.no_response = false";

/**
 * The per-tool rollup carried as properties on `operator_turn_completed`.
 *
 * A rollup rather than a per-tool-call event (BRE-5367 decision): one event per
 * turn keeps volume proportional to conversations rather than to tool calls,
 * and the slowest tool is what a "which tool should we fix" question actually
 * needs. Per-tool p95 distributions would need the per-call event and are a
 * deliberate follow-up.
 */
export interface OperatorToolRollup {
  toolCount: number;
  toolErrorCount: number;
  /**
   * BRE-5754: the tool that failed MOST this turn, and how often - present only
   * when something failed 2+ times.
   *
   * `tool_error_count` alone cannot distinguish "three tools each failed once"
   * from "one tool failed three times", and those cost very different things.
   * A retry loop pays a full MODEL round-trip per attempt: a staging workspace-read
   * turn spent ~3.8s and 2 of its 5 round-trips retrying one tool, which is
   * invisible in the count.
   *
   * Optional and OMITTED rather than zeroed, following `lane` and
   * `priorEntityCount` above: absent means "nothing failed twice", which is the
   * healthy case and should not occupy a field on every event.
   */
  repeatedToolFailure?: { tool: string; failures: number };
  /** Name of the single slowest tool call, or null when the turn used none. */
  slowestTool: string | null;
  slowestToolMs: number;
}

/**
 * BRE-5528 - what the model actually wrote this turn, against what it was told
 * to write.
 *
 * ## Why two token counts and not one
 *
 * `outputTokens` is the sum over every model turn in the agent turn: prose,
 * thinking, and tool-call JSON. That is the number `wait_decode` is paid in, so
 * it is the one to watch when asking whether decode is getting cheaper.
 *
 * `finalOutputTokens` is the last model turn alone - the one that produced the
 * user-facing reply. That is the number the lane's word target can actually
 * move, so it is the one the budget is compared against. Judging the budget on
 * the sum instead would make a six-tool turn breach on tool JSON no prompt
 * wording could shrink, and the breach counter would read high forever with no
 * tuning available to bring it down.
 *
 * Neither is a clean prose measure: `finalOutputTokens` still carries that
 * turn's thinking tokens, which the API does not break out and the prompt has no
 * lever over. {@link ResponseLaneBudget} documents how the threshold accounts
 * for that. Stating it here too because a dashboard reader will otherwise take
 * `final_output_tokens` for reply length, and it is reply length plus thinking.
 */
export interface OperatorOutputUsage {
  /** Summed `output_tokens` across every model turn. Drives `wait_decode`. */
  outputTokens: number;
  /** The final model turn's `output_tokens`. What the budget is judged on. */
  finalOutputTokens: number;
  /** The lane's declared threshold this turn was judged against. */
  budgetTokens: number;
  /** Whether `finalOutputTokens` exceeded `budgetTokens`. */
  budgetBreached: boolean;
}

/**
 * BRE-6952: scalar rollups of a turn's skill evidence. Scalars and distinct
 * lists, because PostHog cannot aggregate inside an array of objects.
 * `skills_matches_shipped` is null on a turn with no body to compare, never a
 * vacuous true.
 */
function skillEvidenceRollups(
  evidence: TurnSkillEvidence,
  deliveryEvidence: SkillsDeliveryEvidence | undefined,
): Record<string, unknown> {
  // A detached turn's loads happen after this event is sent; counts here would be zeros that mean nothing.
  if (evidence.capture === "detached" || deliveryEvidence === undefined) {
    return { skills_evidence_capture: evidence.capture };
  }
  const compared = evidence.loads.filter((l) => l.matchesShipped !== null);
  return {
    skills_delivery_evidence: deliveryEvidence,
    skills_evidence_capture: evidence.capture,
    skills_loaded_count: evidence.loads.length,
    skills_loaded_names: [...new Set(evidence.loads.map((l) => l.skillName))],
    skills_loaded_routes: [...new Set(evidence.loads.map((l) => l.route))],
    skills_carried_names: evidence.carried,
    skills_matches_shipped: compared.length === 0 ? null : compared.every((l) => l.matchesShipped === true),
    skills_evidence_read_errors: evidence.readErrors,
  };
}

/**
 * Build the flat property bag both emitters send. Centralised so a property
 * added for the dashboard cannot land on one surface and not the other, and so
 * every name the saved insights reference is declared in exactly one place.
 */
/**
 * BRE-7956: whether this turn ran in a live session its conversation already
 * had open (`warm`: no CLI spawn, no MCP connect) or had to start one (`cold`),
 * and why. Read beside the phase timings, which is where a warm turn's saving
 * shows up.
 */
export interface TurnLiveSessionInfo {
  reuse: "warm" | "cold";
  /** `warm`, or the miss that made the turn cold (`none`, `options_changed`, ...). */
  reason: string;
  /** On `options_changed`: which spawn-fixed inputs differed. */
  changedComponents?: readonly string[];
  /** This turn's position among the turns its session has served, from 1. */
  turnIndex?: number;
  /** How long the session had been alive when this turn started. */
  ageMs?: number;
  /** How long the session sat idle before this turn. */
  idleMs?: number;
  /** Whether the session was held for the conversation's next turn. */
  kept?: boolean;
  /** `kept`, or why it was retired. */
  keepOutcome?: string;
  /** Whether a warm turn re-registered the brev MCP server to renew its bearer. */
  mcpRebind?: boolean;
  mcpRebindMs?: number;
  /** Sessions this task held when the turn started. */
  heldSessions?: number;
  /**
   * BRE-5754: the turn found a pre-warmed session: `hit` when it ran in it,
   * `miss` when it was retired (the reason says why). Absent otherwise.
   */
  prewarm?: "hit" | "miss";
  /**
   * On a hit: spawn to the turn's spawn decision, read after its setup, so
   * it runs longer than spawn to the turn's receipt by that setup.
   */
  prewarmLeadMs?: number;
  /** On a hit: spawn to MCP connected, absent when the turn came first. */
  prewarmReadyMs?: number;
  /** On a hit: the startup wait taken off this turn, the lesser of the two above. */
  prewarmSavedMs?: number;
  /**
   * BRE-6360: how the brev tools were attached: declared `in_process`, the
   * remote `http` server (kill switch), or `none`. Splits the MCP wait by cause.
   */
  brevToolSurface?: "in_process" | "http" | "none";
  /** BRE-7986: how long this turn waited for the conversation's previous turn to finish saving. */
  priorSettleWaitMs?: number;
}

/**
 * BRE-7986: when the client's stream was finished against when the turn's
 * cleanup (process exit, transcript save, spans) ended, both from the
 * terminal `result`. The gap is time the client no longer waits.
 */
export interface TurnEndTiming {
  /** `result`: the finish was sent on the result; `stream_end`: after the stream ended. */
  finishAt: "result" | "stream_end";
  resultToFinishMs: number;
  resultToCleanupMs: number;
  /** A cleanup step that threw after the finish, by name. */
  cleanupError?: string;
}

/**
 * BRE-7986: what the hook stack cost this turn's tool calls, and how many
 * entries it registered, so a per-task registration's saving is attributable.
 */
export interface TurnHookCostInfo {
  entriesPre: number;
  entriesPost: number;
  entriesFailure: number;
  /** Task-scoped gates left out of this turn's array, comma separated. */
  omittedGates: string;
  toolCalls: number;
  preCallbacksPerCall: number;
  postCallbacksPerCall: number;
  preWindowMsP50: number;
  preWindowMsMax: number;
  preWindowMsTotal: number;
}

export const DELEGATION_TURN_END_REASONS = [
  "ended",
  "parent-continues",
  "mixed-batch",
  "unknown-specialist",
  "kept-open-specialist",
  "import-in-turn",
  "no-reply",
  // The stream had not settled the delegation when the batch was judged.
  "unsettled",
] as const;
/** Why a delegation batch did or did not end the turn. Finite, so a dashboard can group on it. */
export type DelegationTurnEndReason = (typeof DELEGATION_TURN_END_REASONS)[number];

/**
 * BRE-7035: what the parent did after its last delegation came back. On a turn
 * the delegation hook ended, the tail is ~0 and no parent request follows; on
 * one it kept open, the tail is the parent's own closing work.
 */
export interface TurnDelegationInfo {
  /** The hook's last verdict; `none` when no batch held a delegation. */
  turnEnd: DelegationTurnEndReason | "none";
  /** Turn end minus the last parent-level delegation result. */
  postDelegationMs: number;
  /** Parent model requests after that result. */
  postDelegationModelTurns: number;
  /** Whether a specialist's text became the reply. */
  specialistAnswered: boolean;
  /** Total ms the delegation hook held the parent waiting on the stream; 0 when it never waited. */
  hookWaitMs: number;
}

export function buildTurnCompletedProperties(args: {
  surface: OperatorSurface;
  liveSession?: TurnLiveSessionInfo;
  /** BRE-7035: omitted on a turn that delegated nothing. */
  delegation?: TurnDelegationInfo;
  turnEnd?: TurnEndTiming;
  hookCost?: TurnHookCostInfo;
  taskKey: string;
  /**
   * BRE-5440: the response lane this turn spoke in - `action` (report the
   * delta) or `information` (report the state). Carried so the contract can be
   * checked in production rather than assumed: response length split by lane,
   * and whether an action turn dumped state it was not asked for.
   *
   * Optional because it is resolved on the /chat path only; a caller that does
   * not resolve a lane omits the property rather than guessing one.
   */
  lane?: string;
  traceId: string;
  conversationId?: string;
  outcome: OperatorTurnOutcome;
  summary: OperatorWaitSummary;
  signals: OperatorWaitSignals;
  tools: OperatorToolRollup;
  /**
   * Server-side time to the first chunk written to the client.
   *
   * This is **the latency percentile**'s basis (BRE-5523, and see the two-names
   * note in this file's header). It used to say the opposite - "NOT the
   * headline TTFT, the browser's is" - which was true only while denied turns
   * were still inside the percentiles. `outcome` rides this same row, so this
   * is the only one of the two numbers that can honestly exclude a denial, and
   * therefore the only one that may answer "how long does answering take".
   *
   * It is NOT **the experience number**: it starts inside agent-service, so it
   * cannot see queueing, stream setup or network. That is what the browser's
   * `operator_turn_first_response.ttft_ms` is for, and why both are kept - the
   * two are differenced, and client minus server is the cost of everything
   * outside this process.
   */
  ttftServerMs: number | null;
  /**
   * BRE-5754: where the pre-first-token silence went, in three phases summing
   * to `ttft_server_ms`.
   *
   * This is the number users describe as "too long before anything renders".
   * The opening frame lands in under a second; then the pane sits still for
   * 5.5-9 seconds. `ttft_server_ms` measures that window and says nothing about
   * where it goes, so every diagnosis has meant hand-reconstructing a timeline
   * from eight span types.
   *
   * A staging workspace-read turn measured `mcp_ms` at 6,416 of a 6,468ms TTFT - the
   * entire silence, with nothing else happening in it. That is the finding this
   * field exists to make routine rather than archaeological.
   *
   * Optional and omitted when the turn never reached first response: a zeroed
   * breakdown on a turn that never spoke would average in as a fast turn.
   */
  ttftBreakdown?: {
    mcpMs: number;
    setupMs: number;
    /**
     * BRE-7987: request leaving -> first chunk to the client. Absent when the
     * SDK's request-sent mark never landed (the near-zero residual it would
     * otherwise carry is not a measurement of the model).
     */
    modelMs?: number;
    /** BRE-7987: request leaving -> the model's first stream event. */
    modelFirstByteMs?: number;
    /**
     * BRE-7987: which event ended setup. `first_byte` means the SDK sent no
     * request-sent status, so `ttft_setup_*` includes the model's first byte.
     */
    boundary?: "request_sent" | "first_byte";
    /** BRE-7987: the SDK's own `ttft_ms` on the first main-session `message_start`. */
    sdkFirstByteMs?: number;
    /** BRE-5772: the two halves of `setupMs`, either side of MCP connect. */
    setupPreMcpMs: number;
    setupPostMcpMs: number;
    /**
     * BRE-5835: the two halves of `setupMs` by OWNERSHIP — our prompt assembly
     * and tool resolution up to the `query()` call, and the SDK's startup after
     * it. A second partition of the same window, cutting across the pre/post
     * MCP one. `undefined` when the turn never reached `query()`.
     */
    setupOursMs?: number;
    setupSdkMs?: number;
  };
  /**
   * BRE-5772: per-round-trip shape. Every other wait field is a turn-level SUM,
   * which cannot tell one 13-second generation from six 2-second ones.
   */
  /**
   * BRE-5789: billed reasoning tokens for the turn, from
   * `usage.output_tokens_details.thinking_tokens`.
   *
   * The measure `thinking_chars` was mistaken for. That one counts SUMMARY
   * text, which is empty on Sonnet 5 and later (`display` defaults to
   * `"omitted"`) and is not the billed quantity even where it is present.
   */
  thinkingTokens?: number;
  roundTrips?: ReadonlyArray<{
    index: number;
    decodeMs: number | null;
    thinkingTokens: number;
    preModelMs: number | null;
    textChars: number;
    thinkingChars: number;
    toolArgChars: number;
    toolCalls: number;
    producedText: boolean;
    stopReason: string | null;
  }>;
  roundTripRollup?: {
    thinkingChars: number;
    textChars: number;
    toolArgChars: number;
    wasted: number;
    wastedMs: number;
    slowestDecodeMs: number;
  };
  /**
   * BRE-5477: time to the server-emitted OPENING FRAME - the one sentence
   * written onto the stream before the model runs, naming what the turn is about
   * to do. Null on every turn that got no frame (non-conv surfaces, degraded
   * turns, tasks that declare none), which is deliberate: an absent frame must
   * read as absent rather than as a fast one.
   *
   * It is a SEPARATE measure from `ttftServerMs` on purpose. Folding the frame
   * into that one would collapse it to ~50ms on every framed turn and destroy
   * the only record of how long the model actually takes to speak - the number
   * this change does NOT improve. Both are needed to state the result honestly:
   * `ttft_server_ms` unchanged, `time_to_first_frame_ms` ~1s.
   */
  timeToFirstFrameMs?: number | null;
  /**
   * BRE-6843 — when the MODEL first said something, when the ANSWER first
   * became visible, and when it became chat text. Ms from turn start.
   *
   * Three fields because they move independently: the first is the silence this
   * ticket removes, the second is the answer arriving sooner, and the third
   * should NOT move - the model did not get faster, so a drop there means
   * something else changed.
   */
  timeToFirstStatusMs?: number | null;
  timeToFirstAnswerMs?: number | null;
  timeToAnswerCommitMs?: number | null;
  modelTurnCount: number;
  compactionCount: number;
  /**
   * How many issued goal-read signatures were discarded because compaction took
   * away the results they stood for (BRE-5701).
   *
   * A COUNTER, not a boolean, so one property answers both questions a reader
   * has: `sum()` is how much was forgotten, and `countIf(> 0)` is how many turns
   * the fix fired on. Zero on the overwhelming majority of turns, which is the
   * correct reading - it only fires when a workspace-read turn that had already read
   * then compacts.
   */
  readGateClearedReads: number;
  /**
   * BRE-6204: raw `GoalStatus` tokens that reached user-visible chat this turn.
   *
   * REQUIRED, not optional, and emitted including 0 - a field present only when
   * non-zero cannot separate "the fix held" from "the field stopped emitting".
   * Exposure, not incidence. See notes/2026-08-21-BRE-6204.md.
   */
  enumTokenExposure: number;
  /**
   * BRE-5723: how many round-trips in this turn wrote prose.
   *
   * The behavioural measure of interstitial narration. 1 is healthy - prose
   * belongs only in the final message - and anything above it is invisible
   * output paid for at full decode price.
   */
  roundTripsWithText: number;
  /**
   * BRE-7985: the turn's `brev_artifact_apply_ops` writes, the routed
   * `artifact-create` specialist's included (`delegatedCalls`). Pairs with
   * `model_turn_count` to show what a build's round trips were spent on;
   * `roundTrips` counts model messages that wrote, `errors` rejected calls.
   * With `delegatedCalls > 0` it includes the specialist's messages, which
   * `model_turn_count` does not, so it can exceed it on a routed build.
   * Zeros on a turn that wrote nothing; absent only from callers that do not
   * count them.
   */
  artifactWrites?: {
    calls: number;
    ops: number;
    opsMax: number;
    roundTrips: number;
    delegatedCalls: number;
    errors: number;
  };
  /**
   * BRE-5495: how many entities this turn's prompt carried from earlier turns'
   * tool results — the conversation entity digest.
   *
   * Without it the ticket cannot check its own claim. The headline measure is
   * `tool_count` falling on turn 2+, and if it does NOT fall there are three
   * indistinguishable explanations: the digest was empty, the digest was
   * present and the model ignored it, or the change never reached the turn.
   * This separates the first from the other two, and pairs with
   * `uncached_prefill_tokens` to show what the digest costs against what it
   * saves.
   *
   * Optional, and omitted rather than defaulted to 0 when the turn resolved no
   * digest at all — a real zero ("nothing had been read yet") and "this build
   * does not compute one" must not look the same on a dashboard. Same rule
   * `lane` follows above.
   */
  priorEntityCount?: number;
  /**
   * BRE-5528: the turn's output-token usage against its lane budget.
   *
   * Optional, and OMITTED rather than zeroed when absent - the properties are
   * stage-gated off in prod (the initiative's standing no-new-prod-telemetry
   * rule), and a zero would be indistinguishable from a turn where the model
   * genuinely wrote nothing. Same rule `lane` and `priorEntityCount` follow.
   */
  output?: OperatorOutputUsage;
  /**
   * BRE-6899: the stored-session plan and whether the transcript loaded, so the
   * resume rate splits by `surface` on the dashboard. `not_called` is a plan
   * that never reached `load()` (`pin`, `off`, or a turn that ended first).
   * Omitted when the turn never resolved a plan.
   */
  sessionResume?: {
    mode: "resume" | "pin" | "off";
    transcriptLoad: "hit" | "miss" | "not_called";
  };
  /** BRE-9432: the task's declared prefetch and how it ended; omitted when the task declares none. */
  /** BRE-9432: `late` = the turn stopped waiting; `waitedMs` = how long the turn blocked on it. */
  taskPrefetch?: { id: string; outcome: "ok" | "failed" | "timeout" | "late"; waitedMs: number };
  /**
   * comment-block-ok: pre-existing BRE-5939 contract doc; BRE-6929 corrected
   * the `pilotEnabled` paragraph inside it. This is the block a query author
   * reads before writing against these fields, which is why the correction
   * belongs here rather than only in the emitting module.
   *
   * BRE-5939: the turn's Agent Skills, split into OFFERED and TRIGGERED.
   *
   * The split is the whole point. Under progressive disclosure a skill's name
   * and description are always in the prompt (~100 tokens each) while its body
   * loads only if the model invokes it — so "the skill was applied", the single
   * fact the older `settings.skills` provenance records, stops being one fact
   * the moment this mechanism is live. Recording only one of the two would
   * leave that metric quietly answering a different question than it used to.
   *
   * `triggered` is a COUNT rather than a name list, because the pilot needs a
   * RATE over many turns: spontaneous triggering measured 2-in-8 on a squarely
   * matching prompt, so a single-shot "it triggered" passes one time in four.
   *
   * `pilotEnabled` NO LONGER SPLITS AN A/B on the main path (BRE-6929): every
   * non-degraded main-session turn is offered the whole catalogue, so it reads
   * true on all of them and the baseline arm is empty. A saved query filtering
   * `skills_pilot_enabled = true` now selects every such turn. Read it as
   * "skills were available"; measure effect with `triggeredNames`.
   *
   * Optional, and omitted rather than zeroed when the caller resolves no offer
   * — the same rule `lane` and `priorEntityCount` follow, for the same reason:
   * a real zero and "this build does not compute one" must not look identical.
   */
  skills?: {
    offered: readonly string[];
    triggered: number;
    /**
     * WHICH skills were invoked, in order. Not redundant with the count: a turn
     * offers several skills at once and they are expected to behave
     * differently, so a count of 1 cannot say which one fired — and that is
     * precisely the comparison the pilot exists to make. Unlike a mis-shaped
     * schema, this one cannot be repaired later: the turns that already ran
     * cannot be asked again.
     *
     * May be SHORTER than `triggered`: the name is read out of model-authored
     * tool input, so an unreadable one is dropped rather than guessed. The
     * count is recorded independently, so that costs the per-skill breakdown
     * and never the trigger rate.
     */
    triggeredNames: readonly string[];
    /**
     * BRE-6920: `Skill` calls whose BODY reached the model, and the ones that
     * did not - malformed, rejected, or still pending when the turn ended.
     *
     * `triggered` above counts dispatches and keeps that meaning, so the
     * shipped `skills_triggered` series stays comparable. Delivery is a
     * different question and reads the result.
     *
     * Required, not optional-with-a-default: a default would silently restore
     * the dispatch-based reading this exists to replace.
     */
    delivered: number;
    skillCallsUnfulfilled: number;
    pilotEnabled: boolean;
    /**
     * BRE-5969: what the specialist that ANSWERED was offered.
     *
     * `offered` above describes the MAIN session, which on a routed turn is the
     * orchestrator that delegates and never answers - BRE-5939 offers it
     * nothing - so `offered: []` says nothing about the answering agent.
     * `answeringScope` names which of the two the pilot reads.
     *
     * BRE-6920: set only once that specialist has emitted a message. A dispatch
     * is an intention, and a delegation that produced none delivered nothing;
     * `skilledDelegationUnanswered` below carries that case.
     */
    subagentOffered?: readonly string[];
    /**
     * BRE-5969: the union of what EVERY router candidate could have been
     * offered — the menu, not the answer.
     *
     * Named for what it is after review caught the first version reporting this
     * union as `subagentOffered`, i.e. as "the answering agent's". Latent while
     * one task declares skills, and the moment a second does it recreates a
     * milder form of the exact mismeasurement this ticket exists to fix. Kept
     * because it is genuinely useful — it says what was on the table — but it
     * must never be read as what the answering agent got.
     */
    routerMenuOffered?: readonly string[];
    /**
     * The subagent that ANSWERED, resolved from the message that answered - the
     * same value `subagentOffered` and `pilotEnabled` are computed from, so a
     * per-task breakdown cannot disagree with the offer on its own row.
     *
     * `null` when the turn did not route, when no specialist responded, or when
     * the responder arrived under an id we never saw dispatched. A plausible
     * wrong task is what this whole ticket is about, so it is never guessed.
     */
    answeringTask?: string | null;
    /**
     * BRE-6920: where the turn was ROUTED - the first delegation that named a
     * specialist, whether or not it went on to answer.
     *
     * Separate from `answeringTask` above, which used to carry this. They
     * diverge exactly when a dispatch fails and another specialist answers,
     * which is the case worth being able to see.
     */
    routedToTask?: string | null;
    /**
     * BRE-6920: a delegation to a skills-carrying specialist that produced no
     * specialist at all - rejected, errored, or abandoned.
     *
     * Per delegation, never per turn: on a turn where one skilled delegation
     * fails and another answers, `subagentOffered` is correctly set and this
     * must be false. Without it the failure is indistinguishable from a turn
     * that never routed.
     */
    skilledDelegationUnanswered?: boolean;
    answeringScope: "main" | "subagent";
    /**
     * BRE-6952: what the engine's own records show loaded, beside the
     * config-derived fields above, which keep their meaning and history.
     * Absent when the model loop never ran.
     */
    evidence?: TurnSkillEvidence;
    deliveryEvidence?: SkillsDeliveryEvidence;
  };
}): Record<string, unknown> {
  const { summary, signals, tools } = args;
  return {
    surface: args.surface,
    task_key: args.taskKey,
    ...(args.skills
      ? {
          skills_offered: args.skills.offered,
          // Both the list and its length: PostHog cannot aggregate over an
          // array property, so the rate this pilot exists to measure needs a
          // scalar to divide by.
          skills_offered_count: args.skills.offered.length,
          skills_triggered: args.skills.triggered,
          skills_triggered_names: args.skills.triggeredNames,
          // A dispatched Skill call that returned nothing usable is a failure
          // the delivery state cannot show once another skill loaded.
          skills_delivered: args.skills.delivered,
          skills_calls_unfulfilled: args.skills.skillCallsUnfulfilled,
          skills_pilot_enabled: args.skills.pilotEnabled,
          // Which agent's offer the pilot should read. On a routed turn the
          // main session's is empty BY DESIGN and is not the answer.
          skills_answering_scope: args.skills.answeringScope,
          // BRE-6920: the ONE field answering "did the standard reach the model
          // on this turn" - preloaded / loaded-on-demand arrived,
          // offered-not-loaded and delegation-unfulfilled are the failures to
          // alert on, none-offered is not a failure.
          //
          // ORDER IS THE PROPERTY, twice over. `offered` is the PARENT's
          // binding, so testing it first recorded `none-offered` on a fully
          // preloaded turn whenever the parent bound none. And a dispatch that
          // no specialist answered must fall BELOW `triggered`, because a
          // parent that recovers and loads the skill itself did get it.
          skills_delivery:
            args.skills.subagentOffered && args.skills.subagentOffered.length > 0
              ? "preloaded"
              : args.skills.delivered > 0
                ? "loaded-on-demand"
                : args.skills.skilledDelegationUnanswered === true
                  ? "delegation-unfulfilled"
                  : args.skills.offered.length === 0
                    ? "none-offered"
                    : "offered-not-loaded",
          // Emitted on every routed turn, including false. `skills_delivery`
          // hides this case whenever a second specialist rescues the turn, and
          // "how often does a delegation produce no agent" is its own question.
          ...(args.skills.skilledDelegationUnanswered !== undefined
            ? {
                skills_skilled_delegation_unanswered:
                  args.skills.skilledDelegationUnanswered,
              }
            : {}),
          ...(args.skills.subagentOffered
            ? {
                skills_subagent_offered: args.skills.subagentOffered,
                skills_subagent_offered_count: args.skills.subagentOffered.length,
              }
            : {}),
          ...(args.skills.routerMenuOffered
            ? {
                skills_router_menu_offered: args.skills.routerMenuOffered,
                skills_router_menu_offered_count:
                  args.skills.routerMenuOffered.length,
              }
            : {}),
          ...(args.skills.routedToTask !== undefined
            ? { skills_routed_to_task: args.skills.routedToTask }
            : {}),
          ...(args.skills.answeringTask !== undefined
            ? { skills_answering_task: args.skills.answeringTask }
            : {}),
          ...(args.skills.evidence
            ? skillEvidenceRollups(args.skills.evidence, args.skills.deliveryEvidence)
            : {}),
        }
      : {}),
    ...(args.lane ? { lane: args.lane } : {}),
    // BRE-9375: every agent-service turn is Tier 1; Tier 2 never reaches here.
    tier: "tier1",
    latency_lane: classifyLatencyLane({
      tier: "tier1",
      surface: args.surface,
      toolCount: tools.toolCount,
      artifactWriteCalls: args.artifactWrites?.calls ?? 0,
      delegated: args.delegation !== undefined,
    }),
    trace_id: args.traceId,
    ...(args.conversationId ? { conversation_id: args.conversationId } : {}),
    ...(args.sessionResume
      ? {
          session_resume_mode: args.sessionResume.mode,
          session_transcript_load: args.sessionResume.transcriptLoad,
        }
      : {}),
    ...(args.taskPrefetch
      ? {
          task_prefetch_id: args.taskPrefetch.id,
          task_prefetch_outcome: args.taskPrefetch.outcome,
          task_prefetch_waited_ms: args.taskPrefetch.waitedMs,
        }
      : {}),
    outcome: args.outcome,

    turn_total_ms: summary.totalMs,
    wait_mcp_connect_ms: summary.buckets.mcp_connect,
    wait_pre_model_ms: summary.buckets.pre_model,
    wait_decode_ms: summary.buckets.decode,
    wait_tool_ms: summary.buckets.tool,
    wait_compaction_ms: summary.buckets.compaction,
    wait_unattributed_ms: summary.unattributedMs,
    dominant_wait_cause: resolveDominantWaitCause(summary, signals),

    api_retry_count: signals.apiRetryCount,
    uncached_prefill_tokens: signals.uncachedPrefillTokens,
    // BRE-5733: the real opening-request prompt size, to be read against
    // `agent_context_floor.total_tokens`. Omitted when the turn never reached
    // the model - see `firstRequestPromptTokens` on OperatorWaitSignals.
    ...(signals.firstRequestPromptTokens !== undefined
      ? { first_request_prompt_tokens: signals.firstRequestPromptTokens }
      : {}),
    ...(signals.cacheReadTokens !== undefined
      ? { cache_read_tokens: signals.cacheReadTokens }
      : {}),
    ...(signals.cacheWriteTokens !== undefined
      ? { cache_write_tokens: signals.cacheWriteTokens }
      : {}),
    compaction_count: args.compactionCount,
    // BRE-5701: the fix is verifiable from PostHog rather than from a log line.
    // Without it the only evidence the gate ever cleared lives in CloudWatch,
    // which QA cannot query and which makes "did the fix fire?" unanswerable
    // beside the symptom it is supposed to remove.
    read_gate_cleared_reads: args.readGateClearedReads,
    enum_token_exposure: args.enumTokenExposure,
    // BRE-5723. Target is 1. Emitted alongside output_tokens /
    // final_output_tokens so a drop in the invisible-output ratio can be
    // attributed to LESS NARRATION rather than to fewer tool calls - the two
    // are indistinguishable in the token ratio alone.
    round_trips_with_text: args.roundTripsWithText,
    ...(args.artifactWrites
      ? {
          artifact_write_calls: args.artifactWrites.calls,
          artifact_write_ops: args.artifactWrites.ops,
          artifact_write_ops_max: args.artifactWrites.opsMax,
          artifact_write_round_trips: args.artifactWrites.roundTrips,
          artifact_write_delegated_calls: args.artifactWrites.delegatedCalls,
          artifact_write_errors: args.artifactWrites.errors,
        }
      : {}),

    // BRE-5772: what the turn's generation was MADE of, and how much of it went
    // nowhere. `wait_decode_ms` is the largest component of a turn (13,267ms p50
    // of 31,699ms) and was entirely opaque - decode/token is ~10ms on every
    // task, so decode time is a direct function of tokens asked for, and these
    // say what those tokens were.
    //
    // CHARACTERS, not tokens: the API reports one `output_tokens` per message
    // and never breaks it down per block, so a per-block token count does not
    // exist to be read. These measure COMPOSITION (the share that went to
    // thinking vs prose vs tool arguments), which is the decision they serve.
    // Never divide by `wait_decode_ms` and call the result a token rate.
    ...(args.roundTripRollup
      ? {
          // BRE-5789 CORRECTION: this counts the thinking SUMMARY, not thinking.
          // It is 0 on Sonnet 5 and later however much reasoning happened -
          // `display` defaults to "omitted" there, so the block arrives with an
          // empty `thinking` field - and even where a summary IS returned you
          // are billed for the full reasoning, not the summary. Useful only as
          // "how much summary text came back". For cost or share-of-generation,
          // use `thinking_tokens`.
          thinking_chars: args.roundTripRollup.thinkingChars,
          text_chars: args.roundTripRollup.textChars,
          tool_arg_chars: args.roundTripRollup.toolArgChars,
          // Round-trips that produced NO text and called NO tool: a full
          // request-decode cycle spent for nothing. Tool-calling round-trips are
          // deliberately excluded - that is the agent working, and on artifact
          // turns the tool arguments ARE the deliverable. Conflating the two is
          // what produced the retracted "91-97% of output is wasted" claim.
          wasted_round_trips: args.roundTripRollup.wasted,
          wasted_round_trip_ms: args.roundTripRollup.wastedMs,
          // The single slowest generation in the turn. `wait_decode_ms` is a
          // sum; this says whether it was one long one or many short ones.
          slowest_decode_ms: args.roundTripRollup.slowestDecodeMs,
        }
      : {}),
    // The shape itself, for reading ONE turn. Dashboards group on the scalars
    // above; this is what turns "the turn took 31s" into a timeline without a
    // CloudWatch round-trip.
    // BRE-5789: the billed reasoning cost of the turn. `thinking_chars` above
    // measures SUMMARY text and reads 0 on Sonnet 5 and later regardless of how
    // much reasoning happened, so it is not a cost signal - this is.
    ...(args.thinkingTokens != null ? { thinking_tokens: args.thinkingTokens } : {}),
    ...(args.roundTrips?.length ? { round_trips: args.roundTrips } : {}),
    model_turn_count: args.modelTurnCount,

    tool_count: tools.toolCount,
    tool_error_count: tools.toolErrorCount,
    // BRE-5754: flattened to two scalars rather than an object - CloudWatch
    // Insights and PostHog both group far better on a plain string + number
    // than on a nested property.
    ...(tools.repeatedToolFailure
      ? {
          repeated_failure_tool: tools.repeatedToolFailure.tool,
          repeated_failure_count: tools.repeatedToolFailure.failures,
        }
      : {}),
    slowest_tool: tools.slowestTool,
    slowest_tool_ms: tools.slowestToolMs,

    ...(args.priorEntityCount != null
      ? { prior_entity_count: args.priorEntityCount }
      : {}),

    // BRE-5528. `output_budget_breached` is a 0/1 COUNTER rather than a boolean
    // so the dashboard can `sum()` it for a breach count and `avg()` it for a
    // breach rate off the same property - "the counter is ~0" is the reading
    // the ticket tunes against, and a boolean makes that two queries.
    ...(args.output
      ? {
          output_tokens: args.output.outputTokens,
          final_output_tokens: args.output.finalOutputTokens,
          output_budget_tokens: args.output.budgetTokens,
          output_budget_breached: args.output.budgetBreached ? 1 : 0,
        }
      : {}),

    ...(args.ttftServerMs !== null ? { ttft_server_ms: args.ttftServerMs } : {}),
    // BRE-5754: the three phases of the pre-first-token silence, flattened to
    // scalars because Insights and PostHog both group better on plain numbers
    // than on a nested object. They sum to `ttft_server_ms`.
    ...(args.ttftBreakdown
      ? {
          ttft_mcp_ms: args.ttftBreakdown.mcpMs,
          ttft_setup_ms: args.ttftBreakdown.setupMs,
          ...(args.ttftBreakdown.modelMs !== undefined
            ? { ttft_model_ms: args.ttftBreakdown.modelMs }
            : {}),
          ...(args.ttftBreakdown.modelFirstByteMs !== undefined
            ? { ttft_model_first_byte_ms: args.ttftBreakdown.modelFirstByteMs }
            : {}),
          ...(args.ttftBreakdown.boundary !== undefined
            ? { ttft_boundary: args.ttftBreakdown.boundary }
            : {}),
          ...(args.ttftBreakdown.sdkFirstByteMs !== undefined
            ? { ttft_sdk_first_byte_ms: args.ttftBreakdown.sdkFirstByteMs }
            : {}),
          // BRE-5772: `ttft_setup_ms` was summing two disjoint windows either
          // side of MCP connect, which is why no span window ever matched it.
          ttft_setup_pre_mcp_ms: args.ttftBreakdown.setupPreMcpMs,
          ttft_setup_post_mcp_ms: args.ttftBreakdown.setupPostMcpMs,
          // BRE-5835: the same window cut by OWNERSHIP rather than by when —
          // our prompt assembly and tool resolution, versus the SDK's
          // subprocess spawn and init. They partition `ttft_setup_ms`, and the
          // split is what decides whether the fix is faster assembly or a
          // pre-warmed subprocess (BRE-5754). One number could never say.
          //
          // Behind the SAME stage switch as the output-token block above, per
          // its own instruction — "one switch rather than a flag per property,
          // so no future property can be added on one side of the gate and not
          // the other". This is that future property, and the initiative's
          // standing rule is that no new telemetry ships to production.
          //
          // Omitted rather than zeroed when the turn never reached `query()`:
          // a zero would average in as the fastest possible startup.
          ...(args.output?.budgetTokens !== undefined &&
          args.ttftBreakdown.setupOursMs !== undefined
            ? {
                ttft_setup_ours_ms: args.ttftBreakdown.setupOursMs,
                ttft_setup_sdk_ms: args.ttftBreakdown.setupSdkMs,
              }
            : {}),
        }
      : {}),
    ...(args.timeToFirstFrameMs != null
      ? { time_to_first_frame_ms: args.timeToFirstFrameMs }
      : {}),
    // BRE-6843: omitted rather than sent as null when the turn never got there,
    // matching every optional timing above it - a row with no status is a turn
    // that never produced one, and `null` and absent would be two spellings of
    // the same fact in one dashboard.
    ...(args.timeToFirstStatusMs != null
      ? { time_to_first_status_ms: args.timeToFirstStatusMs }
      : {}),
    ...(args.timeToFirstAnswerMs != null
      ? { time_to_first_answer_ms: args.timeToFirstAnswerMs }
      : {}),
    ...(args.timeToAnswerCommitMs != null
      ? { time_to_answer_commit_ms: args.timeToAnswerCommitMs }
      : {}),
    ...(args.liveSession ? liveSessionProperties(args.liveSession) : {}),
    ...(args.delegation
      ? {
          delegation_turn_end: args.delegation.turnEnd,
          post_delegation_ms: args.delegation.postDelegationMs,
          post_delegation_model_turns: args.delegation.postDelegationModelTurns,
          delegation_specialist_answered: args.delegation.specialistAnswered ? 1 : 0,
          delegation_hook_wait_ms: args.delegation.hookWaitMs,
          // The alarm: the hook ended the turn and no specialist text reached
          // chat, so the user got no answer. Expected 0 on every row.
          delegation_turn_end_unanswered:
            args.delegation.turnEnd === "ended" && !args.delegation.specialistAnswered ? 1 : 0,
        }
      : {}),
    ...(args.turnEnd
      ? {
          finish_at: args.turnEnd.finishAt,
          result_to_finish_ms: args.turnEnd.resultToFinishMs,
          result_to_cleanup_ms: args.turnEnd.resultToCleanupMs,
          ...(args.turnEnd.cleanupError ? { post_finish_error: args.turnEnd.cleanupError } : {}),
        }
      : {}),
    ...(args.hookCost
      ? {
          hook_entries_pre: args.hookCost.entriesPre,
          hook_entries_post: args.hookCost.entriesPost,
          hook_entries_failure: args.hookCost.entriesFailure,
          hook_omitted_gates: args.hookCost.omittedGates,
          hook_tool_calls: args.hookCost.toolCalls,
          hook_pre_callbacks_per_call: args.hookCost.preCallbacksPerCall,
          hook_post_callbacks_per_call: args.hookCost.postCallbacksPerCall,
          hook_pre_window_ms_p50: args.hookCost.preWindowMsP50,
          hook_pre_window_ms_max: args.hookCost.preWindowMsMax,
          hook_pre_window_ms_total: args.hookCost.preWindowMsTotal,
        }
      : {}),
  };
}

function liveSessionProperties(info: TurnLiveSessionInfo): Record<string, unknown> {
  return {
    session_reuse: info.reuse,
    session_reuse_reason: info.reason,
    ...(info.changedComponents && info.changedComponents.length > 0
      ? { session_changed_components: info.changedComponents.join(",") }
      : {}),
    ...(info.turnIndex != null ? { live_session_turn_index: info.turnIndex } : {}),
    ...(info.ageMs != null ? { live_session_age_ms: info.ageMs } : {}),
    ...(info.idleMs != null ? { live_session_idle_ms: info.idleMs } : {}),
    ...(info.kept != null ? { live_session_kept: info.kept ? 1 : 0 } : {}),
    ...(info.keepOutcome ? { live_session_keep_outcome: info.keepOutcome } : {}),
    ...(info.mcpRebind != null ? { mcp_rebind: info.mcpRebind ? 1 : 0 } : {}),
    ...(info.mcpRebindMs != null ? { mcp_rebind_ms: info.mcpRebindMs } : {}),
    ...(info.heldSessions != null ? { live_sessions_held: info.heldSessions } : {}),
    ...(info.prewarm ? { live_session_prewarm: info.prewarm } : {}),
    ...(info.prewarmLeadMs != null ? { prewarm_lead_ms: info.prewarmLeadMs } : {}),
    ...(info.prewarmReadyMs != null ? { prewarm_ready_ms: info.prewarmReadyMs } : {}),
    ...(info.prewarmSavedMs != null ? { prewarm_saved_ms: info.prewarmSavedMs } : {}),
    ...(info.brevToolSurface ? { brev_tool_surface: info.brevToolSurface } : {}),
    ...(info.priorSettleWaitMs != null ? { prior_turn_settle_wait_ms: info.priorSettleWaitMs } : {}),
  };
}
